import jwtDecode from 'jwt-decode';
import React, { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useMutation } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import apiManager from '@/api/AxiosInstance';
import { postReferralCode } from '@/api/public';
import AlertModal from '@/components/Common/Modal/AlertModal';
import { DEALER_BLACK_LIST_MEMBER } from '@/const/errorCode';
import { COMMON_TOAST_ERROR, DEALER_BLACK_LIST_MEMBER_MESSAGE } from '@/const/errorMessage';
import { useToastContext } from '@/contexts/Common/ToastContext';
import { useMemberContext } from '@/contexts/Member/MemberContext';
import { getReferralCode } from '@/utils/referralCodeUtils';

export default function LoginHandler() {
  const navigate = useNavigate();
  const { updateReferralCodes, getMember } = useMemberContext();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const errorCode = searchParams.get('errorCode');
  const errorMessage = searchParams.get('errorMessage');
  const deviceToken = localStorage.getItem('deviceToken');
  const [cookies, setCookie, removeCookie] = useCookies(['redirect_url', 'partner']);
  const { showToast } = useToastContext();
  const [isShowDealerBlackListPopup, setIsShowDealerBlackListPopup] = useState(false);

  useEffect(() => {
    if (errorCode === 'OAUTH2_ERROR') {
      showToast(errorMessage || '네이버 로그인 실패', 'error', 'bottom');
      navigate('/login', {
        replace: true,
        state: {
          socialErrorMessage: errorMessage || '네이버 로그인 실패',
        },
      });
    } else if (errorCode === 'DEALER_SOCIAL_LOGIN_NOT_ALLOWED') {
      navigate('/login', {
        replace: true,
      });
    } else if (errorCode === DEALER_BLACK_LIST_MEMBER) {
      setIsShowDealerBlackListPopup(true);
    }
  }, [errorCode, errorMessage]);

  useEffect(() => {
    if (!token || errorCode === 'OAUTH2_ERROR' || errorCode === DEALER_BLACK_LIST_MEMBER) {
      return;
    }

    localStorage.setItem('token', token);

    const partnerCode = cookies.partner;

    if (partnerCode) {
      const code = getReferralCode(partnerCode);
      if (code) {
        postReferralCodeMutation.mutate({ referralCode: code });
      }
    }

    if (deviceToken) {
      apiManager
        .post('/api/v1/fire-base/device-token', { deviceToken: deviceToken })
        .then((response) => {})
        .catch((error) => {});
    }

    // redirect_url을 localStorage에서 먼저 확인 (쿠키보다 우선)
    const redirectUrl = localStorage.getItem('redirect_url') || (cookies && cookies.redirect_url) || null;

    // redirect_url 정리 (쿠키와 localStorage 모두에서 제거)
    if (redirectUrl) {
      localStorage.removeItem('redirect_url');
      if (cookies && cookies.redirect_url) {
        removeCookie('redirect_url', { path: '/' });
      }
    }

    // 회원 정보 조회하여 신규/기존 회원 구분
    try {
      const decodedToken = jwtDecode<Token>(token);
      const memberId = JSON.parse(decodedToken.details.toString())['memberId'];

      apiManager
        .get(`/api/v1/members/${memberId}`)
        .then((response) => {
          // 회원 정보 조회 성공 = 기존 회원
          if (getMember) {
            getMember(memberId);
          }

          // redirect_url이 있으면 해당 경로로, 없으면 홈으로 이동
          navigate(redirectUrl || '/');
        })
        .catch((error) => {
          // 회원 정보 조회 실패 = 신규 회원 (404 또는 다른 에러)
          // 신규 회원은 회원가입 완료 페이지로 이동
          console.log(error);
          navigate('/signup/success');
        });
    } catch (decodeError) {
      // 토큰 디코딩 실패 시 신규 회원으로 간주
      console.log(decodeError);
      navigate('/signup/success');
    }
  }, []);

  const postReferralCodeMutation = useMutation((request: { referralCode: string }) => postReferralCode(request), {
    onSuccess: (response) => {
      updateReferralCodes(response.data);
    },
    onError: (error) => {
      showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
    },
  });

  return (
    <AlertModal
      isOpen={isShowDealerBlackListPopup}
      title="딜러 회원 안내"
      content={<div className="break-keep whitespace-pre-line">{DEALER_BLACK_LIST_MEMBER_MESSAGE}</div>}
      buttonLabel="딜러 회원가입 하기"
      onClose={() => {
        setIsShowDealerBlackListPopup(false);
        navigate('/signup', { replace: true, state: { signUpType: 'DEALER' } });
      }}
    />
  );
}
